import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, SxProps, Typography } from "@mui/material";
import { IAccordionConfig } from "../../Typings/Accordions";
import { ExpandMore } from "@mui/icons-material";

/**
 * Used to quickly make accessible accordions, exposes sx prop to allow portable styling
 *
 * @param Takes accordion config object as prop, outputs accessible accordions depending on config
 * @returns Group of Accordion elements wrapped in a div
 */
export function AccordionGroup({ config, sx }: { config: IAccordionConfig; sx?: SxProps }) {
  const { accordionGroupLabel, accordionA11yId, accordions, arrowPosition } = config;

  return (
    <Box aria-label={accordionGroupLabel} sx={sx}>
      {accordions.map((accordion, idx) => {
        const accordionId = `${accordionA11yId}-${idx}`;
        const hasIcon = accordion.icon ? true : false;
        return (
          <Accordion
            key={idx}
            disableGutters={true}
            elevation={0}
            sx={{
              "&.Mui-expanded::before": {
                opacity: 1,
              },
            }}
            data-test={accordionId}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls={`${accordionId}-content`}
              id={`${accordionId}-header`}
              sx={{
                px: "10px",
                gap: "0.5ch",
                borderRadius: "0.25rem",
                ".MuiAccordionSummary-expandIconWrapper": {
                  order: arrowPosition === "left" ? 1 : 2,
                },
                ".MuiAccordionSummary-content": {
                  display: "flex",
                  alignItems: "center",
                  order: arrowPosition === "left" ? 2 : 1,
                },
                "&:hover": {
                  ".MuiAccordionSummary-content .MuiSvgIcon-root": {
                    color: "primary.main",
                  },
                },
                "&:focus-visible": {
                  backgroundColor: "rgba(0,0,0,0.05)",
                  boxShadow: "0px 0px 0px 2px rgba(0,0,0,0.5)",
                  ".MuiSvgIcon-root": {
                    color: "text.primary",
                  },
                },
              }}
            >
              {hasIcon && (
                <accordion.icon
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.8em",
                    lineHeight: 1,
                    marginRight: "2ch",
                    transition: "color 0.2s ease-in-out",
                  }}
                />
              )}

              <Typography
                sx={{
                  fontWeight: "normal",
                }}
              >
                {accordion.label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <accordion.AccordionChild />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
